// src/pages/BulletinCommerceExterieur.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import { useNavigate } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardMedia,
  Box,
} from '@mui/material';
import { styled, ThemeProvider, createTheme } from '@mui/material/styles';
import Footer from '../navbar/Footer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getPublications } from '../services/publicationService';

function BulletinCommerceExterieur() {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [bulletinsData, setBulletinsData] = useState([]);
  const [filteredBulletins, setFilteredBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'bcem', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            title: item.title,
            link: item.document_url,
            image: item.image_url,
            description: item.description,
            date: new Date(item.date), // utilisé pour le tri et le filtre
            publicationDate: item.publication_date ? new Date(item.publication_date) : null,
          }));
          // Trier par date décroissante (plus récent en premier)
          const sorted = mapped.sort((a, b) => b.date - a.date);
          setBulletinsData(sorted);
          setFilteredBulletins(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);

    let filtered;
    if (year === 'Tous') {
      filtered = [...bulletinsData];
    } else {
      filtered = bulletinsData.filter((bulletin) => {
        const bulletinYear = bulletin.date.getFullYear();
        return bulletinYear === parseInt(year);
      });
    }
    // Trier par date décroissante
    filtered.sort((a, b) => b.date - a.date);
    setFilteredBulletins(filtered);
    setVisibleCount(4); // réinitialiser l'affichage
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme();

  // Années disponibles à partir des données chargées
  const availableYears = useMemo(() => {
    const years = bulletinsData.map((bulletin) => bulletin.date.getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [bulletinsData]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '50px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: '#388E3C' }}>
              Bulletins du Commerce Extérieur de Marchandises
            </Typography>
            <Divider style={{ margin: '20px 0' }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '50px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: '#388E3C' }}>
              Bulletins du Commerce Extérieur de Marchandises
            </Typography>
            <Divider style={{ margin: '20px 0' }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '50px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: '#388E3C' }}>
            Bulletins du Commerce Extérieur de Marchandises
          </Typography>
          <Divider style={{ margin: '20px 0' }} />
          <Typography variant="body1" align="left" gutterBottom>
            Cette section présente les Bulletins du Commerce Extérieur de Marchandises du Burundi, avec des données et analyses clés sur les importations et les exportations.
          </Typography>

          <FormControl fullWidth margin="normal" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Sélectionnez par l'année de publication</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Sélectionnez par l'année de publication"
              variant="outlined"
            >
              <MenuItem value="Tous">Tous les bulletins</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>

          <Grid container spacing={1} style={{ marginTop: '10px' }}>
            {filteredBulletins.slice(0, visibleCount).map((bulletin, index) => (
              <Grid item xs={12} sm={6} md={12} key={index} style={{ marginBottom: '5px' }}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex' }}>
                  <CardMedia
                    component="img"
                    sx={{ width: 51, height: 80, m: 2, cursor: 'pointer' }}
                    image={bulletin.image}
                    alt={bulletin.title}
                    onClick={() => window.open(bulletin.link, '_blank')}
                  />
                  <CardContent style={{ flex: 1, padding: '20px' }}>
                    <Typography
                      variant="h6"
                      component="div"
                      style={{ fontWeight: 'bold', color: 'green', marginBottom: '10px', cursor: 'pointer' }}
                      onClick={() => window.open(bulletin.link, '_blank')}
                    >
                      <DownloadIcon /> {bulletin.title}
                    </Typography>
                    <Typography variant="body2" component="div" style={{ marginBottom: '20px' }}>
                      {bulletin.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredBulletins.length && (
            <Button
              onClick={handleShowMore}
              style={{
                marginTop: '20px',
                marginBottom: '20px',
                backgroundColor: '#388E3C',
                color: 'white',
              }}
            >
              Plus de rapports
            </Button>
          )}
          <br />
          <br />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px', lineHeight: 1.6 }}>
            <strong>Note :</strong> Les bulletins du commerce extérieur de marchandises sont publiés trimestriellement, en général <strong>45 jours</strong> après la fin de chaque trimestre, ce qui correspond aux dates suivantes :
            <ul style={{ listStyleType: 'circle', margin: '10px 0 10px 20px', padding: 0 }}>
              <li><strong>15 février</strong> - Publication des données du quatrième trimestre</li>
              <li><strong>15 mai</strong> - Publication des données du premier trimestre</li>
              <li><strong>15 août</strong> - Publication des données du deuxième trimestre</li>
              <li><strong>15 novembre</strong> - Publication des données du troisième trimestre</li>
            </ul>
          </Typography>

          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <Button
              onClick={() => navigate('/bulletin')}
              style={{
                marginTop: '5px',
                backgroundColor: '#388E3C',
                color: 'white',
              }}
            >
              <ArrowBackIcon style={{ marginRight: '8px' }} />
              Retour à la page des bulletins
            </Button>
          </Box>
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default BulletinCommerceExterieur;